import React, { useEffect } from "react";
import { useState } from 'react'
import { Link } from "react-router-dom";
import {
    MagnifyingGlassIcon,
    PlusIcon,
    UserIcon,
} from '@heroicons/react/24/outline'
import { NoSymbolIcon } from "@heroicons/react/24/solid";
import api from "../../api";
import Table from "../../components/Table";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import PatientModuleLayout from "../../components/patients/layout/PatientModuleLayout";
import SidebarToggleButton from "../../components/layout/SidebarToggleButton";

const roles = [
    'Dr. Residente',
    'Secretaría',
    'Dr. Senior',
    'Administrador',
    'Licenciado',
]

const columns = [
    { name: 'name', displayText: 'Nombre y Apellido', clickable: true },
    { name: 'role', displayText: 'Rol' },
    { name: 'govId', displayText: 'Cédula' },
    { name: 'email', displayText: 'Correo' },
    { name: 'specialty', displayText: 'Especialidad' },
    { name: 'registrationNumber', displayText: 'Reg. Prof N°' },
    { name: 'signature', displayText: 'Firma' },
]

export default function UserPage() {

    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [users, setUsers] = useState([])
    const [search, setSearch] = useState("")
    const [loading, setLoading] = useState(true)
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true)
    const isDesktop = useMediaQuery("(min-width: 1024px)")

    const handleSidebarToggle = () => {
        if (isDesktop) {
            setSidebarOpenDesktop(!sidebarOpenDesktop);
            return;
        }
        setSidebarOpen(true);
    };

    useEffect(() => {
        if (isDesktop) {
            setSidebarOpen(false);
        }
    }, [isDesktop])

    useEffect(() => {
        api.specialties.getMulti().then((specialties) => {
            api.users.getMulti(null, search, null, null, null).then((response) => {
                setUsers(response.filter((array) => { return array.firstName ? true : false }).map((element) => {
                    return ({ ...element, name: element.firstName + ' ' + element.lastName, role: roles[element.role - 1], signature: element.digitalSignature ? 'SI' : 'NO', specialty: element.specialty ? specialties.find((specialty) => specialty.id === (element.specialty))?.name : '' })
                }))
                setLoading(false)
            }
            )
        })
    }, [search])


    return (
        <PatientModuleLayout
            currentScreen="Usuarios"
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            loading={loading}
            header={
                <UserPageHeader
                    search={search}
                    setSearch={setSearch}
                    onToggleSidebar={handleSidebarToggle}
                    showNewButton={users.length > 0}
                />
            }
        >
            <div className="px-4 py-6 pb-24 sm:px-6 lg:px-8 lg:pb-8">
                {users.length ?
                    <>
                        <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-3">
                            Usuarios
                        </h2>
                        <p className="text-sm font-semibold text-slate-600 dark:text-slate-400 mb-4">
                            Total de usuarios registrados: {users.length}
                        </p>

                        <div className="hidden lg:block">
                            <Table module="users" columns={columns} rows={users} actiontext="Editar" />
                        </div>

                        <div className="lg:hidden">
                            <UserListMobile users={users} />
                        </div>
                    </>
                    :
                    <div className="flex min-h-[60vh]">
                        <div className="mx-auto my-auto text-center">
                            <NoSymbolIcon className="h-12 w-12 mx-auto text-gray-400" aria-hidden="true" />
                            <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">No se encontraron resultados</h3>
                            <p className="mt-1 text-sm font-semibold text-gray-500 dark:text-gray-400">Probá con otros datos o ingresá un nuevo usuario.</p>
                            <div className="mt-6">
                                <Link
                                    to="/users/new"
                                    type="button"
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-md text-white bg-primary hover:bg-primary"
                                >
                                    <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                                    Nuevo usuario
                                </Link>
                            </div>
                        </div>
                    </div>
                }
            </div>
            {users.length ? (
                <Link
                    to="/users/new"
                    className="fixed bottom-5 right-5 z-20 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-[1.02] sm:hidden"
                >
                    <PlusIcon className="h-5 w-5" />
                    <span>Nuevo usuario</span>
                </Link>
            ) : null}
        </PatientModuleLayout>
    );
}

function UserPageHeader({ search, setSearch, onToggleSidebar, showNewButton }) {
    const [value, setValue] = useState(search);

    const handleKeyDown = (event) => {
        if (event.code === "Enter") {
            setSearch(event.target.value);
        }
    };

    return (
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
            <div className="flex min-h-[72px] items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-6">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                    <SidebarToggleButton onClick={onToggleSidebar} />

                    <div className="flex min-w-0 flex-1 items-center rounded-xl border border-slate-200 bg-slate-50 px-3 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                        <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-primary" />
                        <input
                            type="search"
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                            onKeyDown={handleKeyDown}
                            className="w-full border-none bg-transparent px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-0"
                            placeholder="Buscar por nombre o número de documento"
                        />
                    </div>
                </div>

                {showNewButton ? (
                    <Link
                        to="/users/new"
                        className="hidden items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm sm:inline-flex"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Nuevo usuario
                    </Link>
                ) : null}
            </div>
        </div>
    );
}

const DetailItem = ({ label, value }) => (
    <div className="min-w-0">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {label}
        </span>
        <div className="mt-0.5 truncate text-sm text-gray-700 dark:text-gray-200">
            {value || (
                <span className="italic text-gray-400 dark:text-gray-500">
                    --
                </span>
            )}
        </div>
    </div>
);

function UserListMobile({ users }) {
    return (
        <ul className="space-y-3">
            {users.map((user) => (
                <li key={user.id}>
                    <Link
                        to={`/users/${user.id}`}
                        className="block rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-900"
                    >
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                                <UserIcon className="h-5 w-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-primary">
                                    {user.name}
                                </p>
                                <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                                    {user.email || "Sin correo"}
                                </p>
                            </div>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-3">
                            <DetailItem label="Rol" value={user.role} />
                            <DetailItem label="Cédula" value={user.govId} />
                            <DetailItem label="Especialidad" value={user.specialty} />
                            <DetailItem label="Firma" value={user.signature} />
                        </div>
                    </Link>
                </li>
            ))}
        </ul>
    );
}
